/**
 * Training Request Form — field names match the backend DTO exactly.
 * Named TrainingCourseForm to stay compatible with existing import paths.
 */
import type { TrainingCategory, TrainingRequestCreateRequest } from "../types";

type Props = {
  values: TrainingRequestCreateRequest;
  onChange: (v: TrainingRequestCreateRequest) => void;
  categories: TrainingCategory[];
  categoriesLoading?: boolean;
  categoriesError?: string | null;
  disabled?: boolean;
};

export function TrainingCourseForm({ values, onChange, categories, categoriesLoading, categoriesError, disabled }: Props) {
  function set<K extends keyof TrainingRequestCreateRequest>(
    k: K,
    v: TrainingRequestCreateRequest[K],
  ) {
    onChange({ ...values, [k]: v });
  }

  return (
    <div className="space-y-4">

      {/* Title — required */}
      <label className="block space-y-1.5">
        <span className="text-sm font-semibold text-slate-700">
          Training Title <span className="text-red-500">*</span>
        </span>
        <input
          className="input"
          value={values.trainingTitle}
          onChange={(e) => set("trainingTitle", e.target.value)}
          placeholder="e.g. Project Management Professional (PMP)"
          required
          disabled={disabled}
        />
      </label>

      {/* Category — required */}
      <div className="space-y-1.5">
        <span className="text-sm font-semibold text-slate-700">
          Category <span className="text-red-500">*</span>
        </span>
        {categoriesError ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            Could not load categories: {categoriesError}. Check that{" "}
            <code className="font-mono">GET /api/hr/training/categories</code> is accessible.
          </div>
        ) : (
          <select
            className="input"
            value={values.categoryId ?? ""}
            onChange={(e) => set("categoryId", e.target.value ? Number(e.target.value) : null)}
            required
            disabled={disabled || categoriesLoading}
          >
            <option value="">
              {categoriesLoading ? "Loading categories…" : "— Select category —"}
            </option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.scope ? ` (${c.scope})` : ""}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Provider */}
      <label className="block space-y-1.5">
        <span className="text-sm font-semibold text-slate-700">Training Provider</span>
        <input
          className="input"
          value={values.providerName ?? ""}
          onChange={(e) => set("providerName", e.target.value || null)}
          placeholder="e.g. Strathmore University"
          disabled={disabled}
        />
      </label>

      {/* Venue */}
      <label className="block space-y-1.5">
        <span className="text-sm font-semibold text-slate-700">Venue / Location</span>
        <input
          className="input"
          value={values.venue ?? ""}
          onChange={(e) => set("venue", e.target.value || null)}
          placeholder="e.g. Nairobi, Kenya or Online"
          disabled={disabled}
        />
      </label>

      {/* Dates */}
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block space-y-1.5">
          <span className="text-sm font-semibold text-slate-700">Start Date</span>
          <input
            type="date"
            className="input"
            value={values.trainingStartDate ?? ""}
            onChange={(e) => set("trainingStartDate", e.target.value || null)}
            disabled={disabled}
          />
        </label>
        <label className="block space-y-1.5">
          <span className="text-sm font-semibold text-slate-700">End Date</span>
          <input
            type="date"
            className="input"
            value={values.trainingEndDate ?? ""}
            min={values.trainingStartDate ?? undefined}
            onChange={(e) => set("trainingEndDate", e.target.value || null)}
            disabled={disabled}
          />
        </label>
      </div>

      {/* Estimated cost */}
      <label className="block space-y-1.5">
        <span className="text-sm font-semibold text-slate-700">Estimated Cost (KES)</span>
        <input
          type="number"
          min={0}
          step="0.01"
          className="input"
          value={values.applicantEstimatedCost ?? ""}
          onChange={(e) =>
            set("applicantEstimatedCost", e.target.value ? Number(e.target.value) : null)
          }
          placeholder="0.00"
          disabled={disabled}
        />
      </label>

      {/* Expected outcome */}
      <label className="block space-y-1.5">
        <span className="text-sm font-semibold text-slate-700">Expected Outcome</span>
        <textarea
          className="input min-h-24 resize-y"
          value={values.expectedOutcome ?? ""}
          onChange={(e) => set("expectedOutcome", e.target.value || null)}
          placeholder="Describe the skills or knowledge you expect to gain…"
          disabled={disabled}
        />
      </label>

    </div>
  );
}
